import { useState, useEffect, useRef } from 'react';
import { fetchRevenueCodes } from '../services/api';
import './RevenueCodeCombobox.css';

/**
 * Typeahead for UB-04 (CMS-1450) revenue codes. Renders the Rev Code input plus a
 * dropdown of matching standard codes, with this facility's most-used codes pinned
 * in a "Frequently used" group on top.
 *
 * - onCodeChange(code): free-text edits to the code field.
 * - onPick(code, description, { explicit }): a code was resolved to a description.
 *     explicit=true  → user clicked a suggestion (parent should fill both fields).
 *     explicit=false → exact-match resolved on blur (parent fills description only if empty).
 */
function RevenueCodeCombobox({ code, facilityId, onCodeChange, onPick, onOpenChange, disabled }) {
  const [open, setOpenState]    = useState(false);
  const [results, setResults]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const blurTimer = useRef(null);
  const openRef   = useRef(false);

  // Wrap open state so the parent can reserve room for the dropdown (the form card
  // clips overflow, so it grows the Revenue card only while a menu is open). The ref
  // dedupes notifications so the parent's open-counter stays balanced.
  const setOpen = (next) => {
    if (openRef.current === next) return;
    openRef.current = next;
    setOpenState(next);
    onOpenChange?.(next);
  };

  // Debounced fetch while the dropdown is open. Re-queries as the biller types.
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    const t = setTimeout(() => {
      fetchRevenueCodes({ q: code, facilityId })
        .then((res) => { if (active) setResults(res.data || []); })
        .catch(() => { if (active) setResults([]); })
        .finally(() => { if (active) setLoading(false); });
    }, 180);
    return () => { active = false; clearTimeout(t); };
  }, [open, code, facilityId]);

  useEffect(() => () => {
    clearTimeout(blurTimer.current);
    if (openRef.current) onOpenChange?.(false);
  }, []);

  // Effective description: a facility's custom override wins over the standard text.
  const descOf = (item) => item.custom_description || item.description;

  // `frequent` is computed server-side: (pinned OR used) AND NOT hidden.
  const frequent = results.filter((r) => r.frequent);
  const others   = results.filter((r) => !r.frequent);
  // Flat order matches what the user sees, so keyboard navigation lines up with the list.
  const ordered  = [...frequent, ...others];

  const pick = (item, explicit) => {
    onPick(item.code, descOf(item), { explicit });
    setOpen(false);
    setHighlight(-1);
  };

  const handleBlur = () => {
    // Delay so an option's onMouseDown fires first.
    blurTimer.current = setTimeout(() => {
      setOpen(false);
      setHighlight(-1);
      // Auto-resolve a fully-typed code to its standard description on the way out.
      const typed = (code || '').trim();
      if (typed) {
        const match = results.find((r) => r.code === typed);
        if (match) onPick(match.code, descOf(match), { explicit: false });
      }
    }, 120);
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { setOpen(true); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, ordered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (highlight >= 0 && ordered[highlight]) { e.preventDefault(); pick(ordered[highlight], true); }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlight(-1);
    }
  };

  const renderOption = (item, idx) => (
    <button
      type="button"
      key={item.code}
      className={`rcc-option ${idx === highlight ? 'rcc-option-active' : ''}`}
      onMouseDown={(e) => { e.preventDefault(); pick(item, true); }}
      onMouseEnter={() => setHighlight(idx)}
    >
      <span className="rcc-option-code">{item.code}</span>
      <span className="rcc-option-desc">{descOf(item)}</span>
      {item.pinned ? (
        <svg className="rcc-option-pin" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-label="Pinned">
          <path d="M14 4V2H7v2h1v6l-2 2v2h5v6h2v-6h5v-2l-2-2V4z" />
        </svg>
      ) : null}
    </button>
  );

  return (
    <div className="rcc-wrap">
      <input
        className="gc-rev-input"
        type="text"
        value={code}
        onChange={(e) => { onCodeChange(e.target.value); if (!open) setOpen(true); setHighlight(-1); }}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder="0000"
        maxLength={4}
        disabled={disabled}
        autoComplete="off"
      />
      {open && (
        <div className="rcc-dropdown">
          {loading ? (
            <div className="rcc-msg">Loading…</div>
          ) : ordered.length === 0 ? (
            <div className="rcc-msg">No matching revenue codes</div>
          ) : (
            <>
              {frequent.length > 0 && <div className="rcc-group-label">Frequently used at this facility</div>}
              {frequent.map((item, i) => renderOption(item, i))}
              {others.length > 0 && frequent.length > 0 && <div className="rcc-group-label">All revenue codes</div>}
              {others.map((item, i) => renderOption(item, frequent.length + i))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default RevenueCodeCombobox;
